import {
  IconBuildingSkyscraper,
  IconHome,
  IconUser,
  IconUserCircle,
} from "@tabler/icons-react";
import Status from "../UI/Status";
import Text, { type Color } from "../UI/Text";
import type { Balance } from "../../models/balance";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
import { BalanceDomain } from "../../utils/constants/balances";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
import styles from "./AllocationChip.module.css";

interface AllocationChipProps {
  balance: Balance;
  showAmount?: boolean;
}

type DomainDescriptor = {
  color: Color;
  icon: typeof IconUser;
  label: string;
};

function useDomainDescriptor(balance: Balance): DomainDescriptor | null {
  const { propertiesById } = useProperties({ withById: true });
  const { propertyFoldersById } = usePropertyFolders({ withById: true });
  const { tenantsById } = useTenants({ withById: true });
  const { profileBailleursById } = useProfileBailleurs({ withById: true });

  if (
    balance.domaine === BalanceDomain.PROFILE_BAILLEUR &&
    balance.id_profile_bailleur != null
  ) {
    return {
      color: "violet",
      icon: IconUserCircle,
      label:
        profileBailleursById[balance.id_profile_bailleur]?.nom_profile ??
        "Bailleur",
    };
  }
  if (balance.domaine === BalanceDomain.TENANT && balance.id_tenant != null) {
    return {
      color: "success",
      icon: IconUser,
      label: tenantsById[balance.id_tenant]?.nom ?? "Locataire",
    };
  }
  if (balance.domaine === BalanceDomain.PROPERTY && balance.id_bien != null) {
    return {
      color: "info",
      icon: IconHome,
      label: propertiesById[balance.id_bien]?.nom ?? "Bien",
    };
  }
  if (
    balance.domaine === BalanceDomain.FOLDER &&
    balance.id_property_folder != null
  ) {
    return {
      color: "info",
      icon: IconBuildingSkyscraper,
      label: propertyFoldersById[balance.id_property_folder]?.nom ?? "Immeuble",
    };
  }
  return null;
}

export default function AllocationChip({
  balance,
  showAmount = true,
}: AllocationChipProps) {
  const descriptor = useDomainDescriptor(balance);
  if (!descriptor) return null;
  const Icon = descriptor.icon;

  return (
    <Status
      as="span"
      color={descriptor.color}
      variant="deci"
      text={
        <>
          <Icon size={14} stroke={1.5} />
          <Text as="span" variant="deci" weight="medium">
            {descriptor.label}
          </Text>
          {showAmount && (
            <Text as="span" variant="deci" className={styles.amount}>
              {currencyFormatter(balance.montant)}
            </Text>
          )}
        </>
      }
    />
  );
}
